import React, {
  Dispatch,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import { useAuthContext, useIsOnScreen } from '@repo/hooks';
import { Key, KeyWithTrigger } from '@repo/components/tooltips';
import * as DM from '@radix-ui/react-dropdown-menu';
import { ExpandingMenu, PopoverMenu } from '@repo/components/menus';
import {
  ArrowRightIcon,
  CogIcon,
  GiftIcon,
  CloverleafIcon,
  SpeechBubbleIcon,
  BookIcon,
} from '@repo/icons';
import styles from './UdoMenu.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { selectUdoMenuSystemAlert } from '@repo/redux/actions/alerts';
import { appVersion } from '@app/config';
import { buildReferralActions } from '@components/utils/referral-sharing';
import analytics from '@repo/utils/helpers/analytics';
import { navigateToSettings, openModal } from '@repo/redux/actions/ui';
import { ProBadge, TeamsBadge } from '@repo/icons/ProductNameBadge';
import { DropdownMenuOption } from '@repo/utils/types';
import { selectNavBarState } from '@repo/redux/reducers/ui/navBar';
import upgradeAction from '@support/upgrade-action';
import { UdoTrigger } from '@repo/components/menus';
import { selectAllActivityNotifications } from '@repo/redux/reducers/data/activityNotifications';
import { addTimeSeparators } from '@components/List/VirtualizedList/utils/time-separators';
import { ActivityItem } from '@repo/utils/models/Activity';
import Button from '@components/Button';
import { useNavigate } from 'react-router-dom';
import { timelineDateDisplay } from '@repo/utils/helpers/Occasion';
import { BodyMediumText, ExtraSmallUppercaseText } from '@repo/components/typography';
import SVGWrapper from '@repo/icons/SVGWrapper';
import {
  fetchMoreActivityNotifications,
  markActivityNotificationsAsSeen,
  markAllActivityNotificationsAsSeen,
} from '@repo/redux/actions/activity';
import { useTheme } from '@repo/themes';
import { setSearchTerm, sortSearch } from '@repo/redux/actions/search';
import { selectGroupCollection } from '@repo/redux/actions/groups';

function AppBuildVersion() {
  const { getIsProPlan, getIsTeamsPlan } = useAuthContext();

  let version = appVersion;
  if (window.app.isElectron && window.desktopIntegrations) {
    version = `${version} (${window.desktopIntegrations.appInfo.version})`;
  }

  return (
    <DM.Label className={cn(styles.header, 'text-small')}>
      <div>Clay</div>
      {getIsProPlan() && <ProBadge />}
      {getIsTeamsPlan() && <TeamsBadge />}
      <div className={styles.build}>{version}</div>
    </DM.Label>
  );
}

const HeartIcon = ({ className }: { className?: string }) => (
  <SVGWrapper width="6" height="6" viewBox="0 -0.5 6 6" className={className}>
    <path
      stroke="none"
      fill="#F1362F"
      d="M.51 2.668a1.554 1.554 0 0 1 0-2.304 1.4 1.4 0 0 1 1.884 0L3 .931l.606-.567a1.4 1.4 0 0 1 1.883 0 1.554 1.554 0 0 1 0 2.304L3 5 .51 2.668Z"
    />
    <path stroke="none" fill="#FFF" d="M1 1h1v1H1z" />
  </SVGWrapper>
);

const DotIcon = ({ className, seen }: { className?: string; seen?: boolean }) => (
  <SVGWrapper
    width="6"
    height="6"
    viewBox="0 0 6 6"
    className={cn(className, styles.indicatorDotIcon, seen && styles.seen)}
  >
    <circle cx="3" cy="3" r="2" stroke="none" />
  </SVGWrapper>
);

const TriangleIcon = ({ className }: { className?: string }) => (
  <SVGWrapper width="6" height="6" viewBox="0 0 6 6" className={className} customColor="#EB515B">
    <path
      stroke="none"
      d="M1.093 5h3.814c.365 0 .593-.257.593-.58a.555.555 0 0 0-.078-.285L3.512.795A.586.586 0 0 0 3.001.5a.587.587 0 0 0-.513.295L.58 4.137a.574.574 0 0 0-.08.283c0 .323.228.58.593.58Z"
    />
  </SVGWrapper>
);

const DiamondIcon = ({ className }: { className?: string }) => {
  const [theme] = useTheme();
  const customColor = theme === 'light' ? 'rgb(249,157,46)' : '#FFBD27';

  return (
    <SVGWrapper
      width="6"
      height="6"
      viewBox="0 0 6 6"
      className={className}
      customColor={customColor}
    >
      <rect
        stroke="none"
        width="3.75"
        height="3.75"
        x="1.125"
        y="1.125"
        rx="0.5"
        transform="rotate(45 3 3)"
      />
    </SVGWrapper>
  );
};

const severityIconMap = {
  low: DotIcon,
  medium: DiamondIcon,
  high: TriangleIcon,
};

const notificationTitle = (item: ActivityItem): ReactNode => {
  let title: ReactNode = null;

  if (item.type === 'import') {
    title = `${item.metadata.importTotal} people imported`;

    if (item.metadata.importTotal === 1) {
      title = '1 person imported';

      if (item.contact) {
        title = `${item.contact.displayName} was imported`;
      }
    }
  }

  if (item.type === 'duplicates') {
    title = 'Possible duplicates found! Check the Duplicates view to review and merge.';

    if (item.metadata.count === 0) {
      title = 'Scan complete, no duplicates found!';
    }
  }

  if (item.type === 'new-workspace-member') {
    title = 'You joined Clay!';
  }

  if (item.type === 'whats-new') {
    title = item.metadata.content;
  }

  return title || item.type;
};

const notificationDetails = (item: ActivityItem): ReactNode => {
  const time = timelineDateDisplay(item.date, 'ago');
  const timeDisplay = <span>{time}</span>;
  let details: ReactNode = timeDisplay;

  if (item.type === 'import') {
    details = (
      <>
        {timeDisplay}
        <span>Via {item.metadata.importName}</span>
      </>
    );
  }

  if (item.type === 'duplicates') {
    details = (
      <>
        {timeDisplay}
        <span>Duplicates</span>
      </>
    );
  }

  if (item.type === 'whats-new') {
    details = (
      <>
        {timeDisplay}
        <span>What's New</span>
      </>
    );
  }

  return details || item.type;
};

const useNotificationClickHandler = (item: ActivityItem) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const groups = useSelector(selectGroupCollection);

  if (item.type === 'duplicates' && item.metadata.count == 0) {
    return undefined;
  }

  if (item.type === 'duplicates') {
    return () => {
      navigate('/people/duplicates');
    };
  }

  if (item.type === 'whats-new' && item.metadata.link) {
    return () => {
      window.open(item.metadata.link, '_blank');
    };
  }

  if (item.type === 'import') {
    if (item.metadata.groupId) {
      if (!groups.items.has(String(item.metadata.groupId))) {
        return undefined;
      }

      return () => {
        navigate(`/group/${item.metadata.groupId}`);
      };
    } else if (item.contact && item.metadata.importTotal === 1) {
      return () => {
        navigate(item.contact.link);
      };
    } else if (item.metadata.importTotal > 1) {
      return () => {
        dispatch(sortSearch('created', 'desc'));
        dispatch(setSearchTerm(`integration:${item.metadata.importSource}`, true));
        navigate('/search');
      };
    }
  }

  return undefined;
};

const ImportActivityItem = ({ item }: { item: ActivityItem }) => {
  const onClick = useNotificationClickHandler(item);
  let icon = <DotIcon className={styles.indicatorIcon} seen={item.seen} />;

  if (item.type === 'new-workspace-member') {
    icon = <HeartIcon className={styles.indicatorIcon} />;
  }

  return (
    <Button
      className={cn(
        styles.notification,
        !!onClick && styles.notificationButton,
        !onClick && styles.notificationNoClick,
      )}
      onClick={onClick}
    >
      {icon}
      <BodyMediumText className={styles.label}>{notificationTitle(item)}</BodyMediumText>
      <ExtraSmallUppercaseText className={styles.details}>
        {notificationDetails(item)}
      </ExtraSmallUppercaseText>
    </Button>
  );
};

const ExpandIcon = () => (
  <SVGWrapper width="14" height="14" viewBox="0 0 14 14" color="textPrimary">
    <path
      className={cn(styles.chevron, styles.topChevron)}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.17"
      d="M4.083 4.959 7 2.625l2.916 2.334"
    />
    <path
      className={cn(styles.chevron, styles.btmChevron)}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.17"
      d="M4.084 9.041 7 11.375l2.917-2.334"
    />
  </SVGWrapper>
);

const ContractIcon = () => (
  <SVGWrapper width="14" height="14" viewBox="0 0 14 14" color="textPrimary">
    <path
      className={cn(styles.chevron, styles.topChevron)}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.17"
      d="M4.084 2.916 7 5.25l2.917-2.334"
    />
    <path
      className={cn(styles.chevron, styles.btmChevron)}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.17"
      d="M4.083 11.084 7 8.75l2.916 2.334"
    />
  </SVGWrapper>
);

function ActivityNotificationsDisplay({
  expanded,
  setExpanded,
}: {
  expanded: boolean;
  setExpanded: Dispatch<boolean>;
}) {
  const dispatch = useDispatch();
  const notifications = useSelector(selectAllActivityNotifications);
  const notificationList = useMemo(
    () => notifications.items.valueSeq().toArray(),
    [notifications.items],
  );
  const notificationsWithTimeSeparators = useMemo(() => {
    return addTimeSeparators(expanded ? notificationList : notificationList.slice(0, 4), 'date');
  }, [notificationList, expanded]);

  const firstItemIsHeader = notificationsWithTimeSeparators.items[0]?.id === 'sectionHeader';
  const hasMore = notificationList.length > 4;

  useEffect(() => {
    const unseenAboveTheFoldItems = notificationList.slice(0, 4).filter((item) => !item.seen);
    if (unseenAboveTheFoldItems.length > 0) {
      dispatch(markActivityNotificationsAsSeen(unseenAboveTheFoldItems.map((item) => item.id)));
    }
  }, []);

  if (!notifications.items.size) {
    return null;
  }

  return (
    <div
      className={cn(
        styles.activityNotificationsContainer,
        (expanded || !hasMore) && styles.notExpandable,
        !expanded && hasMore && styles.expandable,
        !firstItemIsHeader && styles.topMargin,
      )}
    >
      {notificationsWithTimeSeparators.items.map((notification) => {
        if (notification.id === 'sectionHeader') {
          return (
            <ExtraSmallUppercaseText key={notification.label} className={styles.timeSeparator}>
              {notification.label}
            </ExtraSmallUppercaseText>
          );
        }

        return <ImportActivityItem key={notification.id} item={notification} />;
      })}
      {hasMore && !expanded && (
        <Button
          className={cn(styles.listStateButton, styles.expandListButton)}
          onClick={() => setExpanded(true)}
        >
          <ExpandIcon />
          <ExtraSmallUppercaseText className={styles.buttonLabel}>See All</ExtraSmallUppercaseText>
          {!!notifications.unreadCount && (
            <ExtraSmallUppercaseText className={styles.unreadCount}>
              {notifications.unreadCount} Unread
            </ExtraSmallUppercaseText>
          )}
        </Button>
      )}
    </div>
  );
}

const MenuAlert = () => {
  const alert = useSelector(selectUdoMenuSystemAlert);

  if (!alert) {
    return null;
  }

  const AlertIcon = severityIconMap[alert.severity];

  return (
    <Button
      className={cn(styles.systemAlert, styles.notificationButton)}
      onClick={() => window.open(alert.url, '_blank')}
    >
      <div className={cn(styles.alertIcon, styles.indicatorIcon)}>
        <AlertIcon className={styles.iconBlur} />
        <AlertIcon />
      </div>
      <BodyMediumText className={cn(styles.label, styles[`alert_${alert.severity}`])}>
        {alert.label}
      </BodyMediumText>
    </Button>
  );
};

const MenuDisplay = ({
  menuOptions,
  expanded,
  setExpanded,
}: {
  menuOptions: DropdownMenuOption[];
  expanded: boolean;
  setExpanded: Dispatch<boolean>;
}) => {
  const dispatch = useDispatch();
  const notifications = useSelector(selectAllActivityNotifications);
  const bottomOfListRef = useRef<HTMLDivElement>(null);
  const scrollingContainerRef = useRef<HTMLDivElement>(null);

  const bottomOfNotificationsListReached = useIsOnScreen(bottomOfListRef, {
    rootMargin: '0px 0px 300px 0px',
    root: scrollingContainerRef?.current,
  });

  useEffect(() => {
    if (bottomOfNotificationsListReached && notifications.canLoadMore && expanded) {
      dispatch(fetchMoreActivityNotifications());
    }
  }, [
    bottomOfNotificationsListReached,
    notifications.canLoadMore,
    expanded,
    notifications.updated,
  ]);

  return (
    <div ref={scrollingContainerRef} className={cn(styles.menuScrollContainer)}>
      <div className={styles.dropdownMenuContainer}>
        <AppBuildVersion />
        <MenuAlert />
        <div className={styles.dropdownMenu}>
          <ActivityNotificationsDisplay expanded={expanded} setExpanded={setExpanded} />
          <div ref={bottomOfListRef} />
          <DM.Root open modal={false}>
            <ExpandingMenu
              options={menuOptions}
              isOpen
              onOpenAutoFocus={(e) => {
                if (expanded) {
                  e.preventDefault();
                }
              }}
            />
          </DM.Root>
        </div>
        {expanded && (
          <Button
            className={cn(styles.listStateButton, styles.contractListButton)}
            onClick={() => setExpanded(false)}
          >
            <ContractIcon />
            <ExtraSmallUppercaseText className={styles.buttonLabel}>
              See Less
            </ExtraSmallUppercaseText>
          </Button>
        )}
      </div>
    </div>
  );
};

export default function UdoMenu() {
  const dispatch = useDispatch();
  const alert = useSelector(selectUdoMenuSystemAlert);
  const { user, getIsFreePlan } = useAuthContext();
  const { status } = useSelector(selectNavBarState);
  const notifications = useSelector(selectAllActivityNotifications);

  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hasUnreadNotifications = notifications.items.some((item) => !item.seen);
  const [unreadNotifications, setUnreadNotifications] = useState(hasUnreadNotifications);

  const handleHelpCenter = useCallback(() => {
    analytics.track('Feedback Tapped');
    dispatch(openModal('support'));
  }, []);

  const handleSupport = useCallback(() => {
    window.open('https://library.clay.earth/', '_blank');
  }, []);

  const handleKeyboardShortcuts = useCallback(() => {
    window.app.emit('shortcuts:open');
  }, []);

  let menuOptions: DropdownMenuOption[] = [
    {
      title: 'Settings',
      icon: CogIcon,
      action: () => dispatch(navigateToSettings()),
      suffix: <KeyWithTrigger keyChar="," />,
    },
    {
      title: 'Keyboard Shortcuts',
      action: handleKeyboardShortcuts,
      icon: CloverleafIcon,
      suffix: <Key>?</Key>,
    },
    {
      title: 'Invite a Friend',
      icon: GiftIcon,
      submenu: buildReferralActions(user, dispatch),
    },
    { divider: true, disabled: true },
    { title: 'Support', action: handleSupport, icon: BookIcon },
    { title: 'Help Center', action: handleHelpCenter, icon: SpeechBubbleIcon },
  ];

  if (getIsFreePlan()) {
    menuOptions = [
      {
        button: (
          <span>
            <span>
              Upgrade Account
              <ArrowRightIcon color="textPrimary" />
            </span>
            <ProBadge />
          </span>
        ),
        action: upgradeAction(user, dispatch),
        className: styles.upgradeBtn,
        focusedClassName: styles.upgradeBtnFocused,
      },
      ...menuOptions,
    ];
  }

  useEffect(() => {
    setTimeout(() => {
      setUnreadNotifications(hasUnreadNotifications);
    }, 3000);
  }, [hasUnreadNotifications]);

  useEffect(() => {
    if (hasUnreadNotifications) {
      dispatch(markAllActivityNotificationsAsSeen());
    }
  }, [expanded]);

  return (
    <div
      className={cn(styles.wrapper, styles[`navBar-${status}`])}
      key={alert?.id || String(unreadNotifications)}
    >
      <PopoverMenu
        trigger={<UdoTrigger isOpen={isOpen} />}
        menuClassName={styles.udoMenu}
        menu={
          <MenuDisplay
            key={String(expanded)}
            menuOptions={menuOptions}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        }
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        side="bottom"
        align="right"
        onClose={() => setExpanded(false)}
      />
    </div>
  );
}
